/**
 * Shared filterable + sortable move table. Originally lived inside Drawer.tsx;
 * extracted here so ListView can render the same table inside its expanded
 * position rows.
 *
 * Self-contained styles: each instance renders a <style> block. React doesn't
 * dedupe identical style tags, but the browser CSS engine collapses duplicate
 * rules so the perf cost is negligible. A module-level guard makes sure
 * we only inject the stylesheet once across all instances.
 *
 * Optional `onViewDetails` — when provided, each row gets a tiny "view"
 * link in the rightmost column. The drawer doesn't pass it (clicking the
 * row IS view details), but ListView's inner tables do so users can jump
 * straight into the drawer for a specific move/counter.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Move, Position } from '../types/domain';
import { searchMatcher } from './search-text';

const STYLES = `
.bjjmap-filter-input {
  width: 100%;
  background: rgba(255,255,255,0.04);
  color: #e6e8ef;
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  margin-bottom: 8px;
  outline: none;
  transition: border-color 120ms ease, background 120ms ease;
}
.bjjmap-filter-input::placeholder { color: rgba(230,232,239,0.35); }
.bjjmap-filter-input:hover { border-color: rgba(255,255,255,0.18); }
.bjjmap-filter-input:focus {
  border-color: rgba(255,255,255,0.32);
  background: rgba(255,255,255,0.06);
}
.bjjmap-move-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  table-layout: fixed;
}
.bjjmap-move-table th {
  text-align: left;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(230,232,239,0.55);
  padding: 6px 8px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid rgba(255,255,255,0.10);
  transition: color 120ms ease, background 120ms ease;
  white-space: nowrap;
}
.bjjmap-move-table th:hover { color: #ffffff; background: rgba(255,255,255,0.04); }
.bjjmap-move-table td {
  padding: 7px 8px;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  font-size: 12px;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bjjmap-move-table tbody tr {
  cursor: pointer;
  transition: background 100ms ease;
}
.bjjmap-move-table tbody tr:hover { background: rgba(202,211,223,0.09); }
.bjjmap-move-table tbody tr:active { background: rgba(202,211,223,0.16); }
.bjjmap-move-table .sort-arrow { opacity: 0.6; margin-left: 4px; }
.bjjmap-move-table .view-link {
  color: #9be7ff;
  text-decoration: underline;
  text-decoration-style: dotted;
  font-size: 11px;
  cursor: pointer;
}
.bjjmap-move-table .view-link:hover { color: #cdf6ff; text-decoration-style: solid; }
`;

let stylesInjected = false;
function ensureStyles() {
  if (typeof document === 'undefined' || stylesInjected) return;
  const tag = document.createElement('style');
  tag.dataset.bjjmap = 'move-table-styles';
  tag.textContent = STYLES;
  document.head.appendChild(tag);
  stylesInjected = true;
}

type SortKey = 'name' | 'category' | 'partner' | 'difficulty';

const DIFFICULTY_RANK: Record<string, number> = {
  fundamental: 0,
  intermediate: 1,
  advanced: 2,
  specialist: 3,
};

function difficultyChip(d: string | undefined): React.ReactNode {
  if (!d) return <span style={{ opacity: 0.3 }}>—</span>;
  const palette: Record<string, { bg: string; fg: string }> = {
    fundamental: { bg: 'rgba(118, 224, 174, 0.18)', fg: '#9cf2c4' },
    intermediate: { bg: 'rgba(155, 231, 255, 0.18)', fg: '#bfe9ff' },
    advanced: { bg: 'rgba(255, 187, 102, 0.18)', fg: '#ffd29a' },
    specialist: { bg: 'rgba(255, 107, 145, 0.18)', fg: '#ffaabc' },
  };
  const c = palette[d] || { bg: 'rgba(255,255,255,0.08)', fg: '#cad3df' };
  return (
    <span
      style={{
        background: c.bg,
        color: c.fg,
        padding: '2px 7px',
        borderRadius: 8,
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        whiteSpace: 'nowrap',
      }}
    >
      {d}
    </span>
  );
}

function Empty() {
  return <div style={{ opacity: 0.4, fontSize: 12, padding: '4px 0' }}>(none in dataset)</div>;
}

export function MoveTable({
  title,
  direction,
  moves,
  positions,
  onSelect,
  onPreview,
  onViewDetails,
  highlightedId,
}: {
  title: string;
  direction: '→' | '←';
  moves: Move[];
  positions: Position[];
  onSelect: (id: string) => void;
  onPreview?: (id: string | null) => void;
  onViewDetails?: (id: string) => void;
  /** When provided, the matching row gets a tinted background so the
   *  user can see at a glance which move is currently selected — used
   *  by ListView's expanded position panels so an edge selection in
   *  the 3D scene visibly highlights the move row under the position
   *  it lives on. */
  highlightedId?: string | null;
}) {
  useEffect(() => {
    ensureStyles();
  }, []);

  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const enriched = useMemo(
    () =>
      moves.map((m) => {
        const partnerId = direction === '→' ? m.to : m.from;
        const partner = positions.find((p) => p.id === partnerId);
        return { ...m, partnerName: partner?.name ?? partnerId };
      }),
    [moves, positions, direction],
  );

  const filtered = useMemo(() => {
    if (!filter.trim()) return enriched;
    const match = searchMatcher(filter);
    return enriched.filter(
      (m) => match(m.name) || match(m.category) || match(m.partnerName),
    );
  }, [enriched, filter]);

  const sorted = useMemo(() => {
    const out = [...filtered];
    out.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortKey === 'category') cmp = a.category.localeCompare(b.category);
      else if (sortKey === 'difficulty')
        cmp =
          (DIFFICULTY_RANK[a.difficulty ?? ''] ?? 99) - (DIFFICULTY_RANK[b.difficulty ?? ''] ?? 99);
      else cmp = a.partnerName.localeCompare(b.partnerName);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return out;
  }, [filtered, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const Arrow = ({ k }: { k: SortKey }) =>
    sortKey === k ? <span className="sort-arrow">{sortDir === 'asc' ? '↑' : '↓'}</span> : null;

  const partnerHeader = direction === '→' ? 'destination' : 'source';

  return (
    <div style={{ marginTop: 18 }}>
      <h3
        style={{
          fontSize: 11,
          opacity: 0.5,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          margin: '0 0 8px',
        }}
      >
        {title}
      </h3>
      {moves.length === 0 ? (
        <Empty />
      ) : (
        <>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter..."
            className="bjjmap-filter-input"
            onClick={(e) => e.stopPropagation()}
          />
          {sorted.length === 0 ? (
            <div style={{ opacity: 0.4, fontSize: 12, padding: '4px 0' }}>(no matches)</div>
          ) : (
            <table className="bjjmap-move-table">
              {/* Column order: Name | Destination/Source | Type |
                  Difficulty | (view). Partner is promoted to the
                  second slot so the reader's eye reads the move's
                  primary signal (where it lands or where it comes
                  from) immediately after the move's name. Type and
                  difficulty trail because they're meta-data and
                  fit naturally at the row end. */}
              <colgroup>
                {onViewDetails ? (
                  <>
                    <col style={{ width: '30%' }} />
                    <col style={{ width: '24%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '10%' }} />
                  </>
                ) : (
                  <>
                    <col style={{ width: '32%' }} />
                    <col style={{ width: '28%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '22%' }} />
                  </>
                )}
              </colgroup>
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')}>
                    name <Arrow k="name" />
                  </th>
                  <th onClick={() => handleSort('partner')}>
                    {partnerHeader} <Arrow k="partner" />
                  </th>
                  <th onClick={() => handleSort('category')}>
                    type <Arrow k="category" />
                  </th>
                  <th onClick={() => handleSort('difficulty')}>
                    difficulty <Arrow k="difficulty" />
                  </th>
                  {onViewDetails && <th></th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map((m) => (
                  <tr
                    key={m.id}
                    onClick={() => onSelect(m.id)}
                    onMouseEnter={() => onPreview?.(m.id)}
                    onMouseLeave={() => onPreview?.(null)}
                    style={
                      highlightedId === m.id
                        ? { background: 'rgba(155,231,255,0.14)' }
                        : undefined
                    }
                  >
                    <td title={m.name}>{m.name}</td>
                    <td title={m.partnerName} style={{ opacity: 0.85 }}>
                      {m.partnerName}
                    </td>
                    <td style={{ opacity: 0.7 }}>{m.category}</td>
                    <td>{difficultyChip(m.difficulty)}</td>
                    {onViewDetails && (
                      <td style={{ textAlign: 'right' }}>
                        <span
                          className="view-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewDetails(m.id);
                          }}
                        >
                          view
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
